import Logo from "../../assets/images/youtube.webp";
import guideImage from "../../assets/images/guide.webp";
import DateFormat from "./DateFormat";
import GameIconTag from "./GameIconTag";
import { useEffect, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp, faBook, faArrowRight } from "@fortawesome/free-solid-svg-icons";
import GameImageGallery from "../game/GameImageGallery";
import HorizontalAds from "../kosana/HorizontalAds";
import {
  getImageSourceCandidates,
  resolveImageSrc,
  toImageUrl,
} from "../../utils/image";
import WatchlistButton from "./WatchlistButton";

// Games that have a guide hub: games collection id → guide URL segment.
const GUIDE_LINKS: Record<string, string> = {
  "663ba9d6a055571913a15f57": "wuthering-waves",
  "6315694d6b557e56fc6b3b60": "zzz",
};

const EMPTY_GAME = {
  title: "",
  synopsis: "",
  releaseDate: "",
  platform: [],
  gameGenres: [],
  reviewScore: {
    story: 0,
    graphicAndSound: 0,
    gameplay: 0,
    performance: 0,
  },
  newReviewScore: {
    score: 0,
    pros: [],
    crons: [],
  },
  thumbnail: "",
  franchise: [],
  publishers: [],
  developers: [],
  gameNumberPlayer: [],
  imageGallery: [],
  _id: "",
};

// Merge a parent-provided game over the empty shape so arrays are never
// undefined before .map(), and so the card renders in the initial HTML.
// newReviewScore is deliberately left out of the defaults: its presence is what
// selects the new score layout over the legacy 4-part one, and the API omits
// the key entirely for legacy-scored games.
const seedGame = (initialGame: any) => ({
  ...EMPTY_GAME,
  newReviewScore: undefined,
  ...initialGame,
});

const GameDetailCard = (props: any) => {
  const seededGame = props.initialGame ? seedGame(props.initialGame) : null;
  const [game, setGame] = useState<any>(seededGame || EMPTY_GAME);
  const [isLoading, setIsLoading] = useState(!seededGame);
  const [thumbnailError, setThumbnailError] = useState(false);
  const [thumbnailCandidateIndex, setThumbnailCandidateIndex] = useState(0);
  // A hardcoded hub, else the game's published Guide Builder guide (fetched by
  // pages/game/[id].tsx getStaticProps).
  const guideHref: string | undefined = GUIDE_LINKS[props.id] || props.guideSlug || undefined;

  const [currentIndexDesktop, setCurrentIndexDesktop] = useState(0);
  let score: any;
  if (game.reviewScore) {
    score =
      (game.reviewScore.story +
        game.reviewScore.graphicAndSound +
        game.reviewScore.gameplay +
        game.reviewScore.performance) /
      4;
  }
  (Math.round(score * 2) / 2).toFixed(1);
  useEffect(() => {
    // Parent-seeded game (game page SSR, or the game embedded in an article):
    // render it immediately. Re-seed when the id changes under a reused page
    // instance (client-side navigation between two articles).
    if (props.initialGame) {
      if (props.initialGame._id && props.initialGame._id !== game._id) {
        setGame(seedGame(props.initialGame));
        setThumbnailError(false);
        setThumbnailCandidateIndex(0);
      }
      setIsLoading(false);
      // /contents/:id embeds the game one level deep (gameGenres are bare ids).
      // The article card's Genres list needs the names, so fill them in the
      // background without dropping the seeded card. Genres only render in the
      // desktop (Tailwind `md`) layout, so mobile skips the call; and once the
      // API populates them this branch stops fetching on its own.
      const shallow = (props.initialGame.gameGenres || []).some(
        (g: any) => typeof g === "string"
      );
      const desktop = window.matchMedia("(min-width: 768px)").matches;
      if (props.gamePage || !shallow || !desktop) return;
    } else {
      setIsLoading(true);
      setThumbnailError(false);
      setThumbnailCandidateIndex(0);
    }

    let mounted = true;
    fetch(`${process.env.BACKEND_BASE_URL}/games/${props.id}`)
      .then((res) => res.json())
      .then((data) => {
        if (mounted && data?.data) setGame(data.data);
      })
      .catch(() => {
        // Keep the seeded card on failure; only an unseeded card falls back.
        if (mounted && !props.initialGame) setGame(EMPTY_GAME);
      })
      .finally(() => {
        if (mounted) setIsLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [props.id]);
  const normalizedThumbnail = resolveImageSrc(game.thumbnail);
  const thumbnailCandidates = getImageSourceCandidates(game.thumbnail);
  const activeThumbnail =
    thumbnailCandidates[thumbnailCandidateIndex] || normalizedThumbnail;
  const detailThumbnail = isLoading
    ? ""
    : !thumbnailError && activeThumbnail
      ? activeThumbnail
      : guideImage;

  const handleThumbnailError = () => {
    if (thumbnailCandidateIndex < thumbnailCandidates.length - 1) {
      setThumbnailCandidateIndex((prev) => prev + 1);
      return;
    }
    setThumbnailError(true);
  };

  // The game embedded in /contents/:id is populated one level deep, so its
  // gameGenres are bare ids; only entries that carry a title can be named.
  const namedGenres = (game.gameGenres || []).filter(
    (genre: any) => genre && typeof genre === "object" && genre.title
  );

  // One score rule site-wide (ArticleCard, Top 10): 9, 9.5, 9.75. The new
  // layout stores its score as a string; the legacy one averages four parts,
  // and only counts when all four were filled in (unscored games carry nulls).
  const formatScore = (n: number) => String(Math.round(n * 100) / 100);
  const legacyParts = game.reviewScore
    ? [
        game.reviewScore.story,
        game.reviewScore.graphicAndSound,
        game.reviewScore.gameplay,
        game.reviewScore.performance,
      ]
    : [];
  const legacyScored =
    legacyParts.length === 4 &&
    legacyParts.every((v: any) => typeof v === "number" && Number.isFinite(v));
  const newScore = Number(game.newReviewScore?.score);
  const scoreText = game.newReviewScore
    ? game.newReviewScore.score !== "" && Number.isFinite(newScore) && newScore > 0
      ? formatScore(newScore)
      : ""
    : legacyScored && score > 0
      ? formatScore(score)
      : ""; // no review score (or an all-zero legacy one): the score plate hides
  const isReview = props.category === "Review" || props.category === "ReviewBanner";

  // Player modes (Single player, Online …) as surface plates beside the genre
  // plates; like genres they are bare ids when embedded one level deep.
  const namedPlayerModes = (game.gameNumberPlayer || []).filter(
    (mode: any) => mode && typeof mode === "object" && mode.title
  );
  const joinTitles = (items: any[]) =>
    (items || [])
      .map((item: any) => (item && typeof item === "object" ? item.title : ""))
      .filter(Boolean)
      .join(", ") || "–";

  // The cover, whole: covers are 3:4 or 2:3 box art, so they sit in a 3:4
  // frame uncropped (`object-contain`) instead of losing their bottom edge.
  const cover = (sizes: string) => (
    <div className="relative aspect-[3/4] w-full overflow-hidden rounded-[3px] bg-theme-surface">
      {detailThumbnail ? (
        <Image
          src={toImageUrl(detailThumbnail)}
          alt={game.title || "Game cover"}
          fill
          sizes={sizes}
          {...(props.gamePage ? { priority: true } : { loading: "lazy" as const })}
          className="object-contain"
          onError={handleThumbnailError}
        />
      ) : (
        <div className="h-full w-full animate-pulse bg-theme-surface" />
      )}
    </div>
  );

  const platformPlate =
    game.platform && game.platform.length > 0 ? (
      <div className="inline-flex h-9 items-center gap-3 bg-theme-surface px-3">
        <GameIconTag tag={game.platform} />
      </div>
    ) : null;

  const scoreBug = scoreText ? (
    <div className="inline-flex h-9 items-stretch" role="img" aria-label={`Game's On score: ${scoreText}`}>
      <span className="score-bug h-9 text-2xl" aria-hidden="true">{scoreText}</span>
      <span className="-ml-2 flex items-center bg-theme-surface pl-5 pr-3 font-display text-sm font-semibold text-theme-text" aria-hidden="true">
        Game&#39;s On Score
      </span>
    </div>
  ) : null;

  if (isLoading) {
    return (
      <div className="mb-2 w-full rounded-[4px] bg-theme-card p-4 md:p-6">
        <div className="grid grid-cols-[96px_minmax(0,1fr)] gap-4 md:grid-cols-[200px_minmax(0,1fr)] md:gap-8">
          <div className="aspect-[3/4] w-full animate-pulse rounded-[3px] bg-theme-surface" />
          <div className="flex flex-col gap-3">
            <div className="h-8 w-3/4 animate-pulse bg-theme-surface" />
            <div className="h-9 w-40 animate-pulse bg-theme-surface" />
            <div className="hidden h-20 w-full animate-pulse bg-theme-surface md:block" />
          </div>
        </div>
      </div>
    );
  }

  // ── Game page: the game's own header panel ──────────────────────────────
  if (props.gamePage) {
    return (
      <section className="mb-2 w-full rounded-[4px] bg-theme-card" aria-label={game.title || "Game detail"}>
        <div className="grid grid-cols-[112px_minmax(0,1fr)] gap-x-4 gap-y-5 p-4 sm:grid-cols-[148px_minmax(0,1fr)] md:grid-cols-[220px_minmax(0,1fr)] md:grid-rows-[auto_1fr] md:gap-x-8 md:p-6 lg:grid-cols-[248px_minmax(0,1fr)] lg:p-8">
          {/* Cover — spans both rows from md up */}
          <div className="md:row-span-2">
            {cover("(max-width: 639px) 112px, (max-width: 767px) 148px, (max-width: 1023px) 220px, 248px")}
          </div>

          {/* Title, platforms, score */}
          <div className="min-w-0">
            {/* The page's single H1: pages/game/[id].tsx renders none of its
                own now that the card is one responsive tree. */}
            {/* The name on the left, its actions on the right of the same line:
                Guide (when the game has one) then Follow (signed-in readers).
                Where the line is too narrow (phones) the actions wrap under
                the name; empty, and hidden, for signed-out visitors of a game
                without a guide. */}
            <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
            <h1 className="m-0 min-w-0 font-display text-[1.5rem] font-extrabold italic leading-[1.45] tracking-tight text-theme-text sm:text-[1.75rem] md:text-[2.25rem] lg:text-[2.5rem]">
              {game.title}
            </h1>
            <div className="flex min-w-0 flex-wrap items-center gap-2 empty:hidden md:mt-1.5 md:shrink-0 lg:mt-2">
              {guideHref && (
                <Link
                  href={`/game/guide/${guideHref}`}
                  prefetch={false}
                  rel="noopener noreferrer"
                  className="btn-signal group"
                >
                  <FontAwesomeIcon icon={faBook} className="text-sm" />
                  <span>Guide</span>
                  <FontAwesomeIcon icon={faArrowRight} className="text-xs transition-transform duration-150 group-hover:translate-x-0.5" />
                </Link>
              )}
              <WatchlistButton gameId={props.id} />
            </div>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {platformPlate}
              {scoreBug}
            </div>
          </div>

          {/* Tags, synopsis, key facts, actions */}
          <div className="col-span-2 min-w-0 md:col-span-1 md:col-start-2">
            {namedGenres.length > 0 || namedPlayerModes.length > 0 ? (
              <div className="flex flex-wrap gap-1.5">
                {namedGenres.map((genre: { _id: string; title: string }) => (
                  <span key={genre._id} className="tag-plate [--plate-cut:0px]">
                    {genre.title}
                  </span>
                ))}
                {namedPlayerModes.map((mode: { _id: string; title: string }) => (
                  <span
                    key={mode._id}
                    className="inline-flex items-center bg-theme-surface px-2.5 py-0.5 font-display text-[13px] font-semibold leading-6 text-theme-text"
                  >
                    {mode.title}
                  </span>
                ))}
              </div>
            ) : null}

            {game.synopsis ? (
              <p className="mt-4 max-w-[72ch] text-[15px] leading-7 text-theme-text-secondary md:text-base md:leading-7">
                {game.synopsis}
              </p>
            ) : null}

            <dl className="m-0 mt-5 grid grid-cols-1 gap-y-2.5 border-t border-theme-border pt-4 sm:grid-cols-3 sm:gap-x-6">
              <div className="flex items-baseline justify-between gap-4 sm:block">
                <dt className="text-sm font-medium text-theme-text-secondary">Release date</dt>
                <dd className="m-0 text-right text-[15px] font-semibold tabular-nums text-theme-text sm:mt-1 sm:text-left">
                  <DateFormat dateTime={game.releaseDate} />
                </dd>
              </div>
              <div className="flex items-baseline justify-between gap-4 sm:block">
                <dt className="text-sm font-medium text-theme-text-secondary">Publisher</dt>
                <dd className="m-0 text-right text-[15px] font-semibold text-theme-text sm:mt-1 sm:text-left">
                  {joinTitles(game.publishers)}
                </dd>
              </div>
              <div className="flex items-baseline justify-between gap-4 sm:block">
                <dt className="text-sm font-medium text-theme-text-secondary">Developer</dt>
                <dd className="m-0 text-right text-[15px] font-semibold text-theme-text sm:mt-1 sm:text-left">
                  {joinTitles(game.developers)}
                </dd>
              </div>
            </dl>

          </div>
        </div>

        {game.imageGallery && game.imageGallery.length > 0 ? (
          <div className="border-t border-theme-border p-4 md:p-6 lg:p-8">
            <GameImageGallery
              imageData={game.imageGallery}
              currentIndex={currentIndexDesktop}
              setCurrentIndex={setCurrentIndexDesktop}
            ></GameImageGallery>
          </div>
        ) : null}
      </section>
    );
  }

  // ── Article page: the game the story is about (+ the review score) ──────
  return (
    <section className="mb-2 w-full overflow-hidden rounded-[4px] bg-theme-card">
      <div className="grid grid-cols-[96px_minmax(0,1fr)] gap-x-4 gap-y-4 p-4 sm:grid-cols-[120px_minmax(0,1fr)] md:gap-x-6 md:p-6 lg:grid-cols-[150px_minmax(0,1fr)_200px]">
        <Link
          href={`/game/${props.id}`}
          prefetch={false}
          rel="noopener noreferrer"
          aria-label={game.title || "Game detail"}
          className="block self-start"
        >
          {cover("(max-width: 639px) 96px, (max-width: 1023px) 120px, 150px")}
        </Link>

        <div className="min-w-0">
          <Link href={`/game/${props.id}`} prefetch={false} rel="noopener noreferrer" className="group block">
            <p className="m-0 font-display text-xl font-extrabold italic leading-[1.45] tracking-tight text-theme-text transition-colors group-hover:text-signal dark:group-hover:text-[#8DB4FF] md:text-2xl md:leading-[1.45]">
              {game.title}
            </p>
          </Link>
          {game.synopsis ? (
            <p className="mt-2 text-sm leading-6 text-theme-text-secondary md:text-[15px] md:leading-6">
              {game.synopsis}
            </p>
          ) : null}
        </div>

        <div className="col-span-2 flex min-w-0 flex-col items-start gap-4 lg:col-span-1">
          <dl className="m-0 flex w-full flex-col gap-3">
            <div>
              <dt className="text-sm font-medium text-theme-text-secondary">Release date</dt>
              <dd className="m-0 mt-0.5 text-[15px] font-semibold tabular-nums text-theme-text">
                <DateFormat dateTime={game.releaseDate} />
              </dd>
            </div>
            {game.platform && game.platform.length > 0 ? (
              <div>
                <dt className="text-sm font-medium text-theme-text-secondary">Platform</dt>
                <dd className="m-0 mt-1.5 flex flex-wrap items-center gap-2.5">
                  <GameIconTag tag={game.platform} />
                </dd>
              </div>
            ) : null}
            {/* Desktop only: see the genre fill-in in the effect above. */}
            {namedGenres.length > 0 ? (
              <div className="hidden md:block">
                <dt className="text-sm font-medium text-theme-text-secondary">Genres</dt>
                <dd className="m-0 mt-1.5 flex flex-wrap gap-1.5">
                  {namedGenres.map((genre: { _id: string; title: string }) => (
                    <span key={genre._id} className="tag-plate [--plate-cut:0px]">
                      {genre.title}
                    </span>
                  ))}
                </dd>
              </div>
            ) : null}
          </dl>
          <WatchlistButton
            gameId={props.id}
            tooltip="Get notified when new content for this game is published"
          />
        </div>
      </div>

      {isReview ? (
        <div className="border-t border-theme-border">
          {/* The score bug on its panel header, like the scoreboard */}
          <div className="flex items-stretch">
            <p className="m-0 flex flex-1 items-center bg-stage-deep px-4 py-2.5 font-display text-lg font-extrabold italic text-white">
              Game&#39;s On Score
            </p>
            <span className="score-bug min-w-[4.5rem] text-3xl">{scoreText || "–"}</span>
          </div>
          <div className="p-4 md:p-6">
            {game.newReviewScore ? (
              <div className="grid gap-6 md:grid-cols-2">
                <div className="min-w-0">
                  <p className="m-0 font-display text-lg font-extrabold italic text-theme-text">ข้อดี</p>
                  <ul className="m-0 mt-3 flex list-none flex-col gap-2.5 p-0">
                    {(game.newReviewScore?.pros || []).map((text, index) =>
                      text ? (
                        <li key={index} className="flex gap-2.5 text-[15px] leading-6 text-theme-text">
                          <FontAwesomeIcon icon={faChevronUp} className="mt-1.5 shrink-0 text-xs text-signal dark:text-[#8DB4FF]" />
                          <span className="min-w-0 break-words">{text}</span>
                        </li>
                      ) : null
                    )}
                  </ul>
                </div>
                <div className="min-w-0">
                  <p className="m-0 font-display text-lg font-extrabold italic text-theme-text">ข้อสังเกต</p>
                  <ul className="m-0 mt-3 flex list-none flex-col gap-2.5 p-0">
                    {(game.newReviewScore?.crons || []).map((text, index) =>
                      text ? (
                        <li key={index} className="flex gap-2.5 text-[15px] leading-6 text-theme-text">
                          <FontAwesomeIcon icon={faChevronDown} className="mt-1.5 shrink-0 text-xs text-theme-text-secondary" />
                          <span className="min-w-0 break-words">{text}</span>
                        </li>
                      ) : null
                    )}
                  </ul>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-2">
                {[
                  { label: "Story", value: game.reviewScore?.story },
                  { label: "Design", value: game.reviewScore?.graphicAndSound },
                  { label: "Gameplay", value: game.reviewScore?.gameplay },
                  { label: "Performance", value: game.reviewScore?.performance },
                ].map((part) => (
                  <div key={part.label} className="flex flex-col gap-2">
                    <div className="flex items-baseline justify-between gap-3">
                      <span className="text-[15px] font-medium text-theme-text">{part.label}</span>
                      <span className="font-display text-base font-semibold tabular-nums text-theme-text">
                        {part.value ?? "–"}
                      </span>
                    </div>
                    <div className="h-2 w-full bg-theme-surface">
                      <div
                        className="h-2 bg-signal"
                        style={{ width: `${Math.max(0, Math.min(10, Number(part.value) || 0)) * 10}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : null}
    </section>
  );
};

export default GameDetailCard;
